import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { getAnonymousId, segmentTrack } from '@src/functions/segment'
import { getGclid } from '@src/functions/gclid'
import CallToBook from '@src/components/DisposalPage/CallToBook'
import ChatCalendlyModal from '@src/components/DisposalPage/ChatCalendlyModal'
import { reportChatMeetingLinkClicked } from '@src/functions/chat-meeting'
import classes from '@src/styles/disposal/n8n-chat-embed.module.css'

const WEBHOOK_URL = process.env.NEXT_PUBLIC_N8N_CHAT_WEBHOOK_URL ?? ''

// The widget's own session-id key (node_modules/@n8n/chat/dist/chat.bundle.es.js).
// With `loadPreviousSession: false` the widget always takes the
// `r.value = t.sessionId ?? Qu()` branch: it honours a `sessionId` we pass verbatim
// and only mints its own when we pass nothing. We always pass one, because the id
// has to be known *here* — it is reported to Segment on load as the join key (see
// getOrCreateSessionId below) and the widget writes its own from an async function,
// so reading localStorage back after createChat() would be a race.
//
// ⚠️ We never read this key for the live id. The widget writes whatever id it is
// given back into it, from every page, so it always holds the id of whichever chat
// mounted last — reading it would let a paint conversation continue on the oil page
// and vice versa. It is read exactly once, by migrateLegacySessionId.
const WIDGET_SESSION_KEY = 'n8n-chat/sessionId'

// One conversation per service. n8n fixes a session's `service` at row creation and
// picks the service-specific questions off it, so a session carried from one page to
// another would keep the first page's questions, transcript and A/B test.
// `site_env` is fixed at row creation the same way, so an embed with an overridden
// environment gets its own scope — otherwise /paint-disposal/chat (staging) and
// /paint-disposal (production) would share one session, and whichever page started it
// would decide whether the other bills real Stripe and pings the AE.
const sessionScope = (service?: string, siteEnvOverride?: string) =>
  siteEnvOverride ? `${siteEnvOverride}/${service || 'default'}` : service || 'default'
const sessionKey = (scope: string) => `dimension-chat/sessionId/${scope}`
const messagesKey = (scope: string) => `dimension-chat/messages/${scope}`

// Before sessions were per service, only /paint-disposal embedded the chat and its id
// lived in the widget's key. Copied across once so paint visitors mid-conversation keep
// their session; the marker stops a later run from adopting an id another page's widget
// has since written there.
const LEGACY_SERVICE = 'paint-disposal'
const LEGACY_MIGRATED_KEY = 'dimension-chat/legacySessionMigrated'

// Storage throws outright in some privacy modes, and the chat working without a
// sticky session is far better than no chat at all — so every access degrades to an
// in-memory id for the life of the page rather than failing the mount.
const readStoredSessionId = (scope: string): string | null => {
  try {
    return window.localStorage.getItem(sessionKey(scope))
  } catch {
    return null
  }
}

const persistSessionId = (scope: string, id: string) => {
  try {
    window.localStorage.setItem(sessionKey(scope), id)
  } catch {
    // Storage unavailable — the id still holds for this page view.
  }
}

const clearStoredSessionId = (scope: string) => {
  try {
    window.localStorage.removeItem(sessionKey(scope))
    window.localStorage.removeItem(messagesKey(scope))
  } catch {
    // Nothing was stored to begin with.
  }
}

// The visible conversation, saved so a returning visitor sees the session they are about to
// continue. The id above survives a reload, so n8n carries on from its `chat_sessions`
// transcript — without this the widget showed an empty chat that then answered as if mid-way
// through. `loadPreviousSession` can't do it: the Chat Trigger only answers that from a Memory
// node, and WF-1 has none (docs_ignore/N8N_CHAT_WORKFLOW_PLAN.md). Stored with its session id
// so a transcript is never shown against a different session.
type StoredMessage = { id: string; text: string; sender: 'bot' | 'user' }
type StoredConversation = {
  sessionId: string
  /** Epoch ms of the customer's latest message — when n8n last moved the row's `last_activity`. */
  lastActivity: number
  messages: StoredMessage[]
}

// ⚠️ Mirrors WF-3's daily prune (`Filter Older Than 30 Days`), which blanks a session's transcript
// once its `last_activity` is 30 days old. Past that the agent has no history, so a restored chat
// would show a conversation the bot no longer remembers — the session is retired instead. Must
// not exceed WF-3's window; edit both in the same change. Also caps how long customer details
// sit in this browser.
const SESSION_TTL_MS = 30 * 24 * 60 * 60 * 1000

const readStoredConversation = (scope: string): StoredConversation | null => {
  try {
    const raw = window.localStorage.getItem(messagesKey(scope))
    if (!raw) return null
    const stored = JSON.parse(raw) as Partial<StoredConversation> | null
    if (
      typeof stored?.sessionId !== 'string' ||
      typeof stored.lastActivity !== 'number' ||
      !Array.isArray(stored.messages)
    ) {
      return null
    }
    return {
      sessionId: stored.sessionId,
      lastActivity: stored.lastActivity,
      messages: stored.messages.filter(
        (m) => typeof m?.text === 'string' && (m.sender === 'bot' || m.sender === 'user'),
      ),
    }
  } catch {
    return null
  }
}

const readStoredMessages = (scope: string, sessionId: string): StoredMessage[] => {
  const stored = readStoredConversation(scope)
  return stored?.sessionId === sessionId ? stored.messages : []
}

const countUserMessages = (messages: StoredMessage[]) =>
  messages.filter((m) => m.sender === 'user').length

// `lastActivity` only moves when the customer sends something, as n8n's does: a bot reply lands
// seconds later anyway, and the note posted on restore must not keep an old session alive.
const persistMessages = (scope: string, sessionId: string, messages: StoredMessage[]) => {
  const previous = readStoredConversation(scope)
  const sameSession = previous?.sessionId === sessionId
  const lastActivity =
    sameSession && countUserMessages(messages) <= countUserMessages(previous.messages)
      ? previous.lastActivity
      : Date.now()
  const serialised = JSON.stringify({ sessionId, lastActivity, messages })
  try {
    window.localStorage.setItem(messagesKey(scope), serialised)
  } catch {
    // Storage unavailable or full — the conversation just won't survive a reload.
  }
}

const migrateLegacySessionId = () => {
  try {
    const storage = window.localStorage
    if (storage.getItem(LEGACY_MIGRATED_KEY)) return
    const legacy = storage.getItem(WIDGET_SESSION_KEY)
    if (legacy && !storage.getItem(sessionKey(LEGACY_SERVICE))) {
      storage.setItem(sessionKey(LEGACY_SERVICE), legacy)
    }
    storage.setItem(LEGACY_MIGRATED_KEY, '1')
  } catch {
    // Storage unavailable — there is nothing to migrate.
  }
}

const createSessionId = () => {
  const uuid = window.crypto?.randomUUID?.()
  if (uuid) return uuid
  // randomUUID needs a secure context; fall back for plain-http and older browsers.
  // The bitwise ops are the standard v4 formulation, not an oversight.
  /* eslint-disable no-bitwise */
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
  /* eslint-enable no-bitwise */
}

/**
 * This service's conversation session id, reused across reloads and minted here on a
 * first visit. Generating it ourselves rather than letting the widget do it is what
 * makes it reportable: n8n sees this id on every turn, so pairing it with Segment's
 * `anonymousId` on the load event gives each page's chatbot A/B test a join between
 * a client-side exposure and a conversion that only exists server-side.
 */
const getOrCreateSessionId = (scope: string) => {
  migrateLegacySessionId()
  const existing = readStoredSessionId(scope)
  // A session with nothing saved yet (no message sent, or started before conversations were
  // saved) has no known age, so it is kept.
  const stored = readStoredConversation(scope)
  const expired =
    stored?.sessionId === existing && Date.now() - stored.lastActivity >= SESSION_TTL_MS
  if (existing && !expired) return existing
  if (expired) clearStoredSessionId(scope)
  const id = createSessionId()
  persistSessionId(scope, id)
  return id
}

// ⚠️ The n8n Agent Turn system prompt quotes this verbatim (and the persona there is "Lindy",
// introduced here). Edit both in the same change — see docs_ignore/N8N_CHAT_WORKFLOW_PLAN.md.
const GREETING =
  'Hi, I’m Lindy, customer support at Dimension. What are you disposing of and from which zip code? ' +
  'I will provide a quote after you give me a bit of detail. For example, you can type: ' +
  '5 pallets of waste oil in 55-gal drums from zip code 94114.'

// `.chat-message-typing` is the bubble @n8n/chat renders while it waits for a reply — a
// stable, unprefixed class in the package's shipped stylesheet. Ordinary turns come back in
// 3-5s; only a turn that calls the pricing quoter runs long (~45s, plus model retries), so
// a delayed hint fires for those and stays invisible the rest of the time.
const TYPING_INDICATOR_SELECTOR = '.chat-message-typing'
// The customer's own bubble, from the same stylesheet. Drives `Chat Quote - First Message Sent`.
const USER_MESSAGE_SELECTOR = '.chat-message-from-user'
const WAIT_HINT_DELAY_MS = 15000
// Posted into the conversation as a bot bubble once a turn passes WAIT_HINT_DELAY_MS. Client-only:
// n8n never sees it, so it is not in the `chat_sessions` transcript or the AE's Slack copy.
const WAIT_MESSAGE = 'Still working on your quote, please wait. This can take up to a minute.'
// The bot bubble @n8n/chat posts when its request fails (`Wte` in chat.es.js) — in practice a
// long quoting turn outliving the n8n Cloud proxy. The workflow keeps running and its reply has
// nowhere to go, so the generic error reads as "it broke" and invites a follow-up message while
// the turn is still in flight. After a long wait the replacement tells the customer to wait and
// then ask for the quote by name: the agent never learns its reply was lost, so a bare "ok" can
// get "Anything else?" back, while "What's my quote?" makes it restate the price from history.
// A failure before WAIT_HINT_DELAY_MS is not a lost quote (often the first message), so it only
// asks for a resend.
const WIDGET_ERROR_TEXT = 'Error: Failed to receive response'
const RESPONSE_LOST_MESSAGE =
  'Your quote is still being prepared. Please wait a minute, then type "What\'s my quote?" to see it.'
const SEND_FAILED_MESSAGE = "Sorry, that message didn't go through. Please try sending it again."
// Posted when a restored conversation ends on the customer's message: the page closed while their
// turn was in flight, so n8n ran it and wrote the reply to `chat_sessions`, but it never reached
// this browser. As with RESPONSE_LOST_MESSAGE, resending would make n8n answer twice, while asking
// for the reply makes the agent restate it from history. The wait covers a reload mid-quote, when
// the turn is still running and the reply is not in the transcript yet.
const REPLY_MISSED_MESSAGE =
  'Looks like the page closed before my last reply reached you. Please wait a minute, then type "What was your last message?" to see it.'
// The widget's scrollable message list. Its own scroll-to-bottom runs off an internal event bus
// we can't emit on, so an injected message scrolls the list itself.
const CHAT_BODY_SELECTOR = '.chat-body'

// The slice of the widget's chat store the wait message needs. `createChat` returns the Vue app
// and the package's plugin sets `app.config.globalProperties.$chat` to its store
// (node_modules/@n8n/chat/dist/chat.es.js, `qte.install`). Not in the package's typings, so
// every field is optional and checked — if a release drops it, the below-chat hint takes over.
type ChatStore = {
  messages?: { value?: { id: string; text: string; sender: 'bot' | 'user' }[] }
  waitingForResponse?: { value?: boolean }
}
// The widget's message box. Starter chips type into it rather than calling n8n directly, so a
// starter goes through the widget's own send path and shows up as an ordinary customer bubble.
const TEXTAREA_SELECTOR = 'textarea[data-test-id="chat-input"]'
// The static wrapper the widget's Layout puts around the input. Starters are portalled into a
// node prepended to it so they sit directly above the message box. Vue patches only the nodes
// it created, so a foreign first child survives its re-renders.
const FOOTER_SELECTOR = '.chat-footer'

// Lindy sends the AE's Calendly as a markdown link (the n8n prompt's BOOKING A CALL section,
// with the URL from `sales_rotation.ae_calendly_url`). Opening it in an embedded scheduler instead
// of a new tab is what lets the page see the booking and report it to n8n.
const isCalendlyLink = (href: string) => {
  try {
    const { hostname } = new URL(href)
    return hostname === 'calendly.com' || hostname.endsWith('.calendly.com')
  } catch {
    return false
  }
}

// One-tap first messages, shown until the customer sends anything. The chatbot gets half the
// starts of a plain button (docs_ignore/CHATBOT_VS_FORM_ANALYSIS.md §8), so these take the
// blank-box problem out of the first turn. Paint is drawn from the most common first messages
// in n8n `chat_sessions` (Sep 3 – Oct 8, 2026): 5-gal buckets, 1-gal cans, 55-gal drums and
// pallets cover nearly every chat. Oil has almost no chat history yet, so its list is
// provisional and should be revisited against `chat_sessions` once traffic builds.
const STARTERS: Record<string, string[]> = {
  'paint-disposal': [
    'I have 2x 55 gallon drums of latex paint',
    'I have 10x 5 gallon buckets of paint',
    'I have 15x 1 gallon cans of paint',
    'I have a pallet of expired paint',
  ],
  'oil-recycling': [
    'I have 500 gallons of used oil to pump out',
    'I have 2x 55 gallon drums of used oil',
    'I have a 275 gallon tote of used oil',
    'I have 10x 5 gallon buckets of used motor oil',
  ],
}

// One-tap answers for the bot's multiple-choice questions, shown while its latest bubble contains
// `match`. Answering, by chip or by typing, makes the customer's bubble the latest and hides them.
// ⚠️ Each `match` is text the bot asks word for word, so edit both sides in the same change or the
// chips silently stop appearing:
// - `timing`: the PICKUP TIMING section of the n8n Agent Turn system prompt.
// - every other key: a `question` in the n8n `chat_questions` data table, which WF-1's
//   `Build Extra Questions` tells the bot to ask word for word. Matched on a distinctive part,
//   ignoring case and hyphens, because the model still paraphrases now and then ("such as" for
//   "(for example", "oil-based" for "oil based").
// Questions with free-text answers (container size per case) have no entry.
const QUICK_REPLIES: { key: string; match: string; replies: string[] }[] = [
  {
    key: 'timing',
    match: 'When do you need this picked up?',
    replies: [
      'This week',
      'Within 30 days',
      'Sometime this quarter',
      'Just getting a sense of cost',
    ],
  },
  {
    key: 'paint-type',
    match: 'oil based or latex',
    replies: ['Latex', 'Oil', 'Mixed', 'Not sure'],
  },
  {
    key: 'oil-mixed',
    match: 'Is the oil mixed with anything else',
    replies: ['Not mixed', 'Mixed', 'Not sure'],
  },
  {
    key: 'oil-pump-or-pickup',
    match: 'pump the oil out of your tank or containers on-site',
    replies: ['Pump it out on-site', 'Pick up the containers', 'Not sure'],
  },
]

type QuickReplies = (typeof QUICK_REPLIES)[number]

const normalizeForMatch = (text: string) => text.toLowerCase().replace(/-/g, ' ')

// The quick replies for the question the bot is asking right now, if it is asking one of them.
const activeQuickReplies = (store: ChatStore | undefined): QuickReplies | null => {
  const messages = store?.messages?.value
  if (!Array.isArray(messages) || messages.length === 0) return null
  const last = messages[messages.length - 1]
  if (last.sender !== 'bot' || typeof last.text !== 'string') return null
  const text = normalizeForMatch(last.text)
  return QUICK_REPLIES.find(({ match }) => text.includes(normalizeForMatch(match))) ?? null
}

export type N8nChatEmbedProps = {
  /** Service slug, used for analytics tagging and passed to n8n as metadata. */
  service?: string
  /**
   * Overrides `NEXT_PUBLIC_ENV` as the `siteEnv` sent to n8n. The `/chat` test pages pass
   * `'staging'` so deployed tests use Stripe Sandbox, skip the backend webhook and don't
   * ping or CC sales — n8n treats anything other than `'production'` that way.
   */
  siteEnv?: string
}

type StarterChipsProps = {
  host: HTMLElement
  starters: string[]
  onSelect: (text: string) => void
  /** Keeps every chip on one row, scrolling sideways when the panel is too narrow. */
  singleLine?: boolean
}

function StarterChips({ host, starters, onSelect, singleLine }: StarterChipsProps) {
  // `any` for the same reason as components/portal.tsx: @types/react-dom resolves its own
  // nested @types/react 19, whose ReactNode doesn't accept this repo's React 18 JSX.Element.
  const chips: any = (
    <div className={classes.starters}>
      <div
        className={
          singleLine
            ? `${classes.starterList} ${classes.starterListSingleLine}`
            : classes.starterList
        }
      >
        {starters.map((text) => (
          <button
            key={text}
            type='button'
            className={classes.starter}
            onClick={() => onSelect(text)}
          >
            {text}
          </button>
        ))}
      </div>
    </div>
  )
  return createPortal(chips, host)
}

/**
 * Embeds the customer-facing n8n quoting chatbot. Drops into the right-hand hero
 * column of `Pitch` via its `heroAside` slot, replacing the default CTA card.
 * Replaces the previous Lindy iframe embed.
 */
function N8nChatEmbed({ service, siteEnv: siteEnvOverride }: N8nChatEmbedProps) {
  const siteEnv = siteEnvOverride ?? process.env.NEXT_PUBLIC_ENV ?? ''
  const scope = sessionScope(service, siteEnvOverride)
  const mountRef = useRef<HTMLDivElement>(null)
  // Guards against React 18 StrictMode double-invoking the effect in dev.
  const mountedRef = useRef(false)
  // Bumping this remounts the widget with a fresh session.
  const [instance, setInstance] = useState(0)
  const [showWaitHint, setShowWaitHint] = useState(false)
  // Where the chips render: a node inside the widget's footer, created once the widget mounts.
  // Null means no chips. Starters show until the first customer message; quick replies show while
  // the bot is asking one of the QUICK_REPLIES questions.
  const [chipsHost, setChipsHost] = useState<HTMLElement | null>(null)
  const [showStarters, setShowStarters] = useState(true)
  const [quickReplies, setQuickReplies] = useState<QuickReplies | null>(null)
  const starters = STARTERS[service ?? ''] ?? []
  const waitHintTimerRef = useRef<number | null>(null)
  // The session is captured at click time: a reset mid-booking must not re-tag the booking.
  const [calendly, setCalendly] = useState<{ link: string; sessionId: string } | null>(null)

  useEffect(() => {
    if (mountedRef.current || !mountRef.current || !WEBHOOK_URL) return undefined
    mountedRef.current = true
    const target = mountRef.current
    let cancelled = false

    // @n8n/chat calls `messageEl.scrollIntoView({ block: 'start' })` on every new
    // message bubble. Native scrollIntoView walks up *every* scrollable ancestor
    // to satisfy that alignment — not just the widget's own message list, but
    // this page too — so each send/reply yanked the whole page down. The widget
    // already keeps its own message list pinned to the bottom via
    // `chatBody.scrollTop = chatBody.scrollHeight`, so it's safe to just swallow
    // scrollIntoView calls that originate from inside the widget.
    const originalScrollIntoView = Element.prototype.scrollIntoView
    Element.prototype.scrollIntoView = function patchedScrollIntoView(
      this: Element,
      ...args: Parameters<typeof originalScrollIntoView>
    ) {
      if (target.contains(this)) return
      originalScrollIntoView.apply(this, args)
    }

    // A long turn otherwise shows nothing but bouncing dots, which reads as "it's broken"
    // — the biggest source of confusion in testing. The widget exposes no lifecycle events
    // (`createChat` is its only export), so watch its typing indicator instead and, once the
    // wait stops looking normal, post a bot message into the conversation through the widget's
    // store. Without a usable store it falls back to a hint below the chat. Presentational
    // only: if the widget's internals ever change, both simply stop appearing.
    let chatStore: ChatStore | undefined

    const postWaitMessage = () => {
      const messages = chatStore?.messages?.value
      if (!Array.isArray(messages) || typeof chatStore?.waitingForResponse?.value !== 'boolean') {
        return false
      }
      // The reply may have landed in the same tick the timer fired; don't post after it.
      if (!chatStore.waitingForResponse.value) return true
      messages.push({ id: createSessionId(), text: WAIT_MESSAGE, sender: 'bot' })
      window.requestAnimationFrame(() => {
        const body = target.querySelector(CHAT_BODY_SELECTOR)
        if (body) body.scrollTop = body.scrollHeight
      })
      return true
    }

    // Set when the current wait passes WAIT_HINT_DELAY_MS; cleared once the typing indicator goes.
    // The widget pushes its error bubble and drops the indicator in the same render, and the
    // observer rewrites before it clears, so the flag still describes the failed request.
    let waitedLong = false

    // Through the store so Vue re-renders the bubble; editing the DOM would be overwritten.
    const rewriteFailedResponses = () => {
      const messages = chatStore?.messages?.value
      if (!Array.isArray(messages)) return
      messages.forEach((message) => {
        if (message.sender === 'bot' && message.text === WIDGET_ERROR_TEXT) {
          // eslint-disable-next-line no-param-reassign
          message.text = waitedLong ? RESPONSE_LOST_MESSAGE : SEND_FAILED_MESSAGE
        }
      })
    }

    const clearWaitHintTimer = () => {
      if (waitHintTimerRef.current !== null) {
        window.clearTimeout(waitHintTimerRef.current)
        waitHintTimerRef.current = null
      }
    }

    // "Started a chat" is the step between the chatbot loading and n8n reporting an
    // outcome, and without it the chatbot tests can't tell a chat nobody engages
    // with from one people start and abandon. The widget has no send event, so the first
    // customer bubble is the signal — once per widget instance, since a reset mints a new
    // session that counts as a new chat. A restored conversation already had its first
    // message tracked on the visit that sent it, so the restore sets this up front.
    let firstMessageTracked = false

    // Set once the widget mounts. The wait message is left out: it describes a request that
    // will not be in flight when the conversation is restored.
    let sessionIdForSave: string | null = null
    // Starts as an empty list so an untouched chat writes nothing.
    let lastSaved = '[]'
    const saveMessages = () => {
      const messages = chatStore?.messages?.value
      if (!sessionIdForSave || !Array.isArray(messages)) return
      const toSave = messages
        .filter((m) => m.text !== WAIT_MESSAGE)
        .map(({ id, text, sender }) => ({ id, text, sender }))
      const serialised = JSON.stringify(toSave)
      if (serialised === lastSaved) return
      lastSaved = serialised
      persistMessages(scope, sessionIdForSave, toSave)
    }

    const typingObserver = new MutationObserver(() => {
      rewriteFailedResponses()
      saveMessages()
      // QUICK_REPLIES entries are module constants, so an unchanged question keeps the same object
      // and React skips the re-render.
      setQuickReplies(activeQuickReplies(chatStore))
      if (!firstMessageTracked && target.querySelector(USER_MESSAGE_SELECTOR)) {
        firstMessageTracked = true
        setShowStarters(false)
        segmentTrack('Chat Quote - First Message Sent', {
          service,
          provider: 'n8n',
          sessionId: readStoredSessionId(scope) ?? '',
          anonymousId: getAnonymousId(),
        })
      }
      if (target.querySelector(TYPING_INDICATOR_SELECTOR)) {
        if (waitHintTimerRef.current === null) {
          waitHintTimerRef.current = window.setTimeout(() => {
            waitedLong = true
            if (!postWaitMessage()) setShowWaitHint(true)
          }, WAIT_HINT_DELAY_MS)
        }
        return
      }
      waitedLong = false
      clearWaitHintTimer()
      setShowWaitHint(false)
    })
    typingObserver.observe(target, { childList: true, subtree: true })

    // Capture phase, so the scheduler opens before the widget's rendered anchor navigates.
    // Modified clicks (new tab, etc.) are left alone and open calendly.com untracked.
    const handleLinkClick = (event: MouseEvent) => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return
      }
      const anchor = (event.target as Element | null)?.closest?.('a[href]')
      if (!(anchor instanceof HTMLAnchorElement) || !isCalendlyLink(anchor.href)) return
      event.preventDefault()
      const sessionId = readStoredSessionId(scope) ?? ''
      segmentTrack('Chat Quote - Meeting Link Clicked', {
        service,
        provider: 'n8n',
        sessionId,
        anonymousId: getAnonymousId(),
      })
      reportChatMeetingLinkClicked(sessionId)
      setCalendly({ link: anchor.href, sessionId })
    }
    target.addEventListener('click', handleLinkClick, true)

    // Dynamic import: the package is browser-only and must not execute during SSR.
    import('@n8n/chat')
      .then(({ createChat }) => {
        if (cancelled) return
        const sessionId = getOrCreateSessionId(scope)
        const app = createChat({
          webhookUrl: WEBHOOK_URL,
          target,
          mode: 'fullscreen',
          sessionId,
          loadPreviousSession: false,
          showWelcomeScreen: false,
          enableStreaming: false,
          initialMessages: [GREETING],
          metadata: {
            service: service ?? '',
            pageUrl: window.location.href,
            referrer: document.referrer,
            gclid: getGclid(),
            siteEnv,
            // Additive to the v1 shared contract. The n8n side may ignore it, but
            // forwarding it on the completion webhook is what lets a conversion be
            // joined back to this visitor's `Experiment Viewed` event — otherwise
            // each chatbot A/B test has a treatment funnel that ends
            // server-side and a control funnel that ends client-side, with nothing
            // in common. See docs_ignore/N8N_CHAT_FRONTEND_PLAN.md.
            anonymousId: getAnonymousId(),
          },
          i18n: {
            en: {
              title: 'Get a free quote in less than 2 minutes',
              subtitle: '',
              inputPlaceholder: 'Describe what you need picked up…',
              getStarted: 'New conversation',
              footer: '',
              closeButtonTooltip: 'Close chat',
              repostButton: 'Repost message',
              reuseButton: 'Reuse message',
            },
          },
        })
        chatStore = (app?.config?.globalProperties as { $chat?: ChatStore } | undefined)?.$chat
        const restored = readStoredMessages(scope, sessionId)
        if (restored.length > 0 && chatStore?.messages && Array.isArray(chatStore.messages.value)) {
          chatStore.messages.value.push(...restored)
          if (restored[restored.length - 1].sender === 'user') {
            chatStore.messages.value.push({
              id: createSessionId(),
              text: REPLY_MISSED_MESSAGE,
              sender: 'bot',
            })
          }
          if (restored.some((m) => m.sender === 'user')) {
            firstMessageTracked = true
            setShowStarters(false)
          }
          window.requestAnimationFrame(() => {
            const body = target.querySelector(CHAT_BODY_SELECTOR)
            if (body) body.scrollTop = body.scrollHeight
          })
        }
        sessionIdForSave = sessionId
        // `sessionId` is the durable half of the join key: n8n stamps it on every
        // turn, so a conversion it reports server-side can be traced back to this
        // visitor even when `anonymousId` came back empty because localStorage was
        // unavailable.
        segmentTrack('Chat Quote - Chatbot Loaded', {
          service,
          provider: 'n8n',
          sessionId,
          anonymousId: getAnonymousId(),
        })
        const footer = target.querySelector(FOOTER_SELECTOR)
        if (footer) {
          const host = document.createElement('div')
          footer.prepend(host)
          setChipsHost(host)
        }
      })
      .catch(() => {
        segmentTrack('Chat Quote - Chatbot Load Failed', { service, provider: 'n8n' })
      })

    return () => {
      cancelled = true
      mountedRef.current = false
      typingObserver.disconnect()
      target.removeEventListener('click', handleLinkClick, true)
      clearWaitHintTimer()
      setShowWaitHint(false)
      setChipsHost(null)
      setShowStarters(true)
      setQuickReplies(null)
      Element.prototype.scrollIntoView = originalScrollIntoView
      target.innerHTML = ''
    }
  }, [service, siteEnv, scope, instance])

  // The widget pins its message list to the bottom when the reply lands, and the chips render
  // after that, growing the footer and shrinking the list, so they would cover the question they
  // answer. Re-pin once they are in the DOM.
  useEffect(() => {
    if (!quickReplies) return undefined
    const frame = window.requestAnimationFrame(() => {
      const body = mountRef.current?.querySelector(CHAT_BODY_SELECTOR)
      if (body) body.scrollTop = body.scrollHeight
    })
    return () => window.cancelAnimationFrame(frame)
  }, [quickReplies])

  const handleReset = useCallback(() => {
    segmentTrack('Chat Quote - New Conversation', {
      service,
      provider: 'n8n',
      // The session being retired, so the abandoned conversation stays traceable.
      sessionId: readStoredSessionId(scope) ?? '',
    })
    clearStoredSessionId(scope)
    if (mountRef.current) mountRef.current.innerHTML = ''
    mountedRef.current = false
    setInstance((n) => n + 1)
  }, [service, scope])

  // The textarea is a Vue `v-model`, which only picks up a value from an `input` event; its Enter
  // handler then sends exactly as if the customer had typed it. If the widget's internals change,
  // the text is still left in the box for the customer to send.
  const sendAsCustomer = useCallback((text: string) => {
    const textarea = mountRef.current?.querySelector<HTMLTextAreaElement>(TEXTAREA_SELECTOR)
    if (!textarea) return false
    textarea.value = text
    textarea.dispatchEvent(new Event('input', { bubbles: true }))
    textarea.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true }))
    return true
  }, [])

  const handleStarter = useCallback(
    (text: string) => {
      if (!sendAsCustomer(text)) return
      segmentTrack('Chat Quote - Starter Clicked', {
        service,
        provider: 'n8n',
        starter: text,
        sessionId: readStoredSessionId(scope) ?? '',
        anonymousId: getAnonymousId(),
      })
    },
    [service, scope, sendAsCustomer],
  )

  const handleQuickReply = useCallback(
    (text: string) => {
      if (!sendAsCustomer(text)) return
      segmentTrack('Chat Quote - Quick Reply Clicked', {
        service,
        provider: 'n8n',
        question: quickReplies?.key ?? '',
        reply: text,
        sessionId: readStoredSessionId(scope) ?? '',
        anonymousId: getAnonymousId(),
      })
    },
    [service, scope, sendAsCustomer, quickReplies],
  )

  return (
    <div className={classes.chatContainer}>
      <CallToBook formId={service} from='chatbot' />
      <div key={instance} ref={mountRef} className={classes.chatMount} />
      {chipsHost && showStarters && starters.length > 0 && (
        <StarterChips host={chipsHost} starters={starters} onSelect={handleStarter} />
      )}
      {chipsHost && !showStarters && quickReplies && (
        <StarterChips
          host={chipsHost}
          starters={quickReplies.replies}
          onSelect={handleQuickReply}
          singleLine
        />
      )}
      {/* Always mounted so the live region is present before the text swaps in. */}
      <div className={classes.waitHintRegion} role='status' aria-live='polite'>
        {showWaitHint && (
          <p className={classes.waitHint}>
            Still working on this — pulling your quote can take up to a minute. Please keep this
            page open.
          </p>
        )}
      </div>
      <button type='button' className={classes.resetButton} onClick={handleReset}>
        Start a new conversation
      </button>
      <ChatCalendlyModal
        link={calendly?.link ?? null}
        sessionId={calendly?.sessionId ?? ''}
        service={service}
        onClose={() => setCalendly(null)}
      />
    </div>
  )
}

export default N8nChatEmbed
